import { Action, Reducer } from 'redux';
import * as Actions from '../actions';
import * as ActionEvents from '../actions/PersonRelationships.Actions';
import { PersonRelationshipState, PersonRelationshipUnloadState } from '../states/PersonRelationshipState';
import { PersonRelationshipViewModel } from '../../models/Entities/Person/Relationship/PersonRelationshipModels';

export type KnownAction =
    | Actions.PersonRelationships.PersonRelationshipGetAllRequestAction
    | Actions.PersonRelationships.PersonRelationshipGetAllSuccessAction
    | Actions.PersonRelationships.PersonRelationshipGetAllFailureAction
    | Actions.PersonRelationships.PersonRelationshipGetByIdRequestAction
    | Actions.PersonRelationships.PersonRelationshipGetByIdSuccessAction
    | Actions.PersonRelationships.PersonRelationshipGetByIdFailureAction
    | Actions.PersonRelationships.PersonRelationshipCreateRequestAction
    | Actions.PersonRelationships.PersonRelationshipCreateSuccessAction
    | Actions.PersonRelationships.PersonRelationshipCreateFailureAction
    | Actions.PersonRelationships.PersonRelationshipUpdateRequestAction
    | Actions.PersonRelationships.PersonRelationshipUpdateSuccessAction
    | Actions.PersonRelationships.PersonRelationshipUpdateFailureAction
    | Actions.PersonRelationships.PersonRelationshipDeleteRequestAction
    | Actions.PersonRelationships.PersonRelationshipDeleteSuccessAction
    | Actions.PersonRelationships.PersonRelationshipDeleteFailureAction;

export const PersonRelationshipsReducer: Reducer<PersonRelationshipState> = (
    state: PersonRelationshipState | undefined,
    incomingAction: Action
): PersonRelationshipState => {
    if (state === undefined) {
        return PersonRelationshipUnloadState as PersonRelationshipState;
    }

    const action = incomingAction as KnownAction;

    switch (action.type) {
        case ActionEvents.PERSON_RELATIONSHIP_GETALL_REQUEST:
            return {
                ...state,
                isLoadingAll: true,
                successLoadingAll: undefined,
                failOnLoadingAll: false,
                error: undefined
            };
        case ActionEvents.PERSON_RELATIONSHIP_GETALL_SUCCESS: {
            const otherPersons = state.list.filter((item: PersonRelationshipViewModel) => item.personId !== action.personId);
            return {
                ...state,
                isLoadingAll: false,
                successLoadingAll: true,
                failOnLoadingAll: false,
                list: [...otherPersons, ...action.payload],
                error: undefined
            };
        }
        case ActionEvents.PERSON_RELATIONSHIP_GETALL_FAILURE:
            return {
                ...state,
                isLoadingAll: false,
                successLoadingAll: false,
                failOnLoadingAll: true,
                error: action.error
            };

        case ActionEvents.PERSON_RELATIONSHIP_GETBYID_REQUEST:
            return {
                ...state,
                isLoadingOne: true,
                successLoadingOne: undefined,
                failOnLoadingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_RELATIONSHIP_GETBYID_SUCCESS: {
            const filtered = state.list.filter((item: PersonRelationshipViewModel) => item.id !== action.payload.id);
            return {
                ...state,
                isLoadingOne: false,
                successLoadingOne: true,
                failOnLoadingOne: false,
                list: [...filtered, action.payload],
                error: undefined
            };
        }
        case ActionEvents.PERSON_RELATIONSHIP_GETBYID_FAILURE:
            return {
                ...state,
                isLoadingOne: false,
                successLoadingOne: false,
                failOnLoadingOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_RELATIONSHIP_CREATE_REQUEST:
            return {
                ...state,
                isAddingNewOne: true,
                successAddingNewOne: undefined,
                failOnAddingNewOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_RELATIONSHIP_CREATE_SUCCESS: {
            const createdList = state.list.some((item: PersonRelationshipViewModel) => item.id === action.payload.id)
                ? state.list.map((item: PersonRelationshipViewModel) => (item.id === action.payload.id ? action.payload : item))
                : [...state.list, action.payload];
            return {
                ...state,
                isAddingNewOne: false,
                successAddingNewOne: true,
                failOnAddingNewOne: false,
                lastCreated: action.payload,
                list: createdList,
                error: undefined
            };
        }
        case ActionEvents.PERSON_RELATIONSHIP_CREATE_FAILURE:
            return {
                ...state,
                isAddingNewOne: false,
                successAddingNewOne: false,
                failOnAddingNewOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_RELATIONSHIP_UPDATE_REQUEST:
            return {
                ...state,
                isUpdatingOne: true,
                successUpdatingOne: undefined,
                failOnUpdatingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_RELATIONSHIP_UPDATE_SUCCESS: {
            const updatedList = state.list.some((item: PersonRelationshipViewModel) => item.id === action.payload.id)
                ? state.list.map((item: PersonRelationshipViewModel) => (item.id === action.payload.id ? { ...item, ...action.payload } : item))
                : [...state.list, action.payload];
            return {
                ...state,
                isUpdatingOne: false,
                successUpdatingOne: true,
                failOnUpdatingOne: false,
                list: updatedList,
                error: undefined
            };
        }
        case ActionEvents.PERSON_RELATIONSHIP_UPDATE_FAILURE:
            return {
                ...state,
                isUpdatingOne: false,
                successUpdatingOne: false,
                failOnUpdatingOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_RELATIONSHIP_DELETE_REQUEST:
            return {
                ...state,
                isDeletingOne: true,
                successDeletingOne: undefined,
                failOnDeletingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_RELATIONSHIP_DELETE_SUCCESS:
            return {
                ...state,
                isDeletingOne: false,
                successDeletingOne: true,
                failOnDeletingOne: false,
                list: state.list.filter((item: PersonRelationshipViewModel) => item.id !== action.id),
                error: undefined
            };
        case ActionEvents.PERSON_RELATIONSHIP_DELETE_FAILURE:
            return {
                ...state,
                isDeletingOne: false,
                successDeletingOne: false,
                failOnDeletingOne: true,
                error: action.error
            };
        default:
            return state;
    }
};
